import type { Session, User } from '@supabase/supabase-js'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { setCurrencySource } from '@/domain'
import { supabase } from '@/data/supabase'
import type { Database } from '@/data/database.types'

import { useBudgetStore } from './budget'
import { useLockStore } from './lock-store'
import { disablePush } from './push'
import type { SessionFacts } from './navigation'

type Profile = Database['public']['Tables']['profiles']['Row']

/**
 * Session de l'utilisateur. Source unique de « qui est connecté » pour toute l'application.
 * La session est restaurée depuis le stockage local au démarrage, sans attendre le réseau.
 */
export const useSessionStore = defineStore('session', () => {
  const session = ref<Session | null>(null)
  const profile = ref<Profile | null>(null)
  // Devise du profil : tous les montants affichés la suivent, aussitôt qu'elle change.
  setCurrencySource(() => profile.value?.currency ?? 'XOF')
  const recovering = ref(false)
  const isAdmin = ref(false)
  const ready = ref(false)

  const user = computed<User | null>(() => session.value?.user ?? null)

  const facts = computed<SessionFacts>(() => ({
    signedIn: session.value !== null,
    onboardingCompleted: Boolean(profile.value?.onboarding_completed_at),
    isAdmin: isAdmin.value,
    recovering: recovering.value,
  }))

  async function loadProfile(): Promise<void> {
    if (!user.value) {
      profile.value = null
      isAdmin.value = false
      return
    }
    // La RLS ne renvoie la ligne que si l'utilisateur est lui-même administrateur.
    const admin = await supabase
      .from('admins')
      .select('user_id')
      .eq('user_id', user.value.id)
      .maybeSingle()
    if (!admin.error) isAdmin.value = admin.data !== null
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.value.id)
      .maybeSingle()
    // Hors ligne, on garde le profil déjà connu plutôt que de bloquer l'application.
    if (!error) profile.value = data
  }

  let starting: Promise<void> | null = null

  /** Démarre la session une seule fois ; les appels suivants attendent le même démarrage. */
  function start(): Promise<void> {
    starting ??= (async () => {
      // Écoute posée avant la lecture de la session : l'événement PASSWORD_RECOVERY émis à
      // l'ouverture d'un lien de réinitialisation ne doit pas être manqué.
      supabase.auth.onAuthStateChange((event, next) => {
        session.value = next
        if (event === 'PASSWORD_RECOVERY') recovering.value = true
        if (event === 'SIGNED_OUT') {
          recovering.value = false
          profile.value = null
          // Aucune donnée d'un compte ne doit rester visible pour le suivant.
          useBudgetStore().reset()
        }
        if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
          // Différé : la documentation Supabase déconseille d'attendre une requête dans ce rappel.
          setTimeout(() => void loadProfile(), 0)
        }
      })

      const { data } = await supabase.auth.getSession()
      session.value = data.session
      await loadProfile()
      ready.value = true
    })()
    return starting
  }

  /** Code de réinitialisation vérifié : la session ne sert qu'à choisir un nouveau mot de passe. */
  function startRecovery(): void {
    recovering.value = true
  }

  function endRecovery(): void {
    recovering.value = false
  }

  async function signOut(): Promise<void> {
    // Les notifications affichent des montants : un appareil déconnecté n'en reçoit plus.
    await disablePush().catch(() => undefined)
    useLockStore().stop()
    await supabase.auth.signOut()
    session.value = null
    profile.value = null
    recovering.value = false
    isAdmin.value = false
    useBudgetStore().reset()
  }

  return {
    session,
    user,
    profile,
    isAdmin,
    ready,
    facts,
    start,
    loadProfile,
    startRecovery,
    endRecovery,
    signOut,
  }
})
